import Drafts from "@mui/icons-material/Drafts";
import InsertEmoticonRounded from "@mui/icons-material/InsertEmoticonRounded";
import Send from "@mui/icons-material/Send";
import TouchApp from "@mui/icons-material/TouchApp";
import Warning from "@mui/icons-material/Warning";
import { Grid, Paper, Skeleton } from "@mui/material";
import type { FC } from "react";

import {
  type ChannelName,
  isChatChannel,
  isEmailChannel,
} from "../../../../modules/channels/types";
import type { MomentAnalyticsTotals } from "../../../../modules/stats/server/analytics-service";
import { AnalyticsOverviewClicks } from "./item/clicks";
import { AnalyticsOverviewItem } from "./item/item";
import { AnalyticsOverviewPercentage } from "./item/percentage";
import { AnalyticsOverviewReactions } from "./item/reactions";

export const AnalyticsOverviewContent: FC<{
  loading: boolean;
  data: MomentAnalyticsTotals | null;
  channel_name: ChannelName;
  target_channel: boolean;
}> = ({ loading, data, channel_name, target_channel }) => {
  if (loading || !data) {
    return (
      <Skeleton
        data-testid="analytics-overview-skeleton"
        animation="wave"
        variant="rounded"
        height={100}
      />
    );
  }

  return (
    <Paper elevation={6} sx={{ px: 3, py: 2 }}>
      <Grid container spacing={8}>
        <AnalyticsOverviewItem
          title="Delivered"
          icon={<Send color="primary" fontSize="small" />}
          tooltip="Messages that were delivered successfully for every time this Communication has been sent."
        >
          <AnalyticsOverviewPercentage
            data={{ count: data.delivery_count, total: data.total_sends }}
          />
        </AnalyticsOverviewItem>
        {isEmailChannel(channel_name) && (
          <AnalyticsOverviewItem
            title="Unique Opens"
            icon={<Drafts color="primary" fontSize="small" />}
            tooltip="Messages that were opened by unique individuals for every time this Communication has been sent."
          >
            <AnalyticsOverviewPercentage
              data={{ count: data.first_open_count ?? 0, total: data.delivery_count ?? 0 }}
            />
          </AnalyticsOverviewItem>
        )}
        {!target_channel && (
          <AnalyticsOverviewItem
            title="Interacted"
            icon={<TouchApp color="primary" fontSize="small" />}
            tooltip="Messages where recipients clicked on a link/button for every time this Communication has been sent."
          >
            <AnalyticsOverviewPercentage
              data={{
                count: data.click_count ?? 0,
                total:
                  (isEmailChannel(channel_name) ? data.first_open_count : data.delivery_count) ?? 0,
              }}
            />
          </AnalyticsOverviewItem>
        )}
        {target_channel && (
          <AnalyticsOverviewItem
            title="Clicks"
            icon={<TouchApp color="primary" fontSize="small" />}
            tooltip="Total number of link and button clicks by all recipients in the channel."
          >
            <AnalyticsOverviewClicks data={{ count: data.total_clicks }} />
          </AnalyticsOverviewItem>
        )}
        {isChatChannel(channel_name) && (
          <AnalyticsOverviewItem
            title="Reactions"
            icon={<InsertEmoticonRounded color="primary" fontSize="small" />}
            tooltip="Emoji reactions to the message, for every time this Communication has been sent."
          >
            <AnalyticsOverviewReactions reactions={data.reaction_summary} />
          </AnalyticsOverviewItem>
        )}
        <AnalyticsOverviewItem
          title="Issues"
          icon={<Warning color="primary" fontSize="small" />}
          tooltip="Individual delivery issues for every time this Communication has been sent."
        >
          <AnalyticsOverviewPercentage
            data={{ count: data.error_count ?? 0, total: data.total_sends }}
          />
        </AnalyticsOverviewItem>
      </Grid>
    </Paper>
  );
};
